/**
 * 远程控制访客 token 管理模块，负责获取与缓存。
 */
import { getGuestToken } from "@/services/remote/signaling";
import { getJwtExp } from "@/utils/jwt";

const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;
const TOKEN_STORAGE_KEY = "remote_control_guest_token";
const TOKEN_EXP_STORAGE_KEY = "remote_control_guest_token_exp";

let cachedGuestToken = null;
let cachedGuestTokenExp = 0;
let pendingTokenPromise = null;

/**
 * 获取可用 token，如临近过期则触发刷新。
 * @returns {Promise<string|null>} 可用 token
 */
export const getValidGuestToken = async () => {
	if (!shouldRefreshGuestToken()) {
		return Promise.resolve(cachedGuestToken);
	}

	if (!pendingTokenPromise) {
		pendingTokenPromise = fetchAndCacheGuestToken().finally(() => {
			pendingTokenPromise = null;
		});
	}

	return pendingTokenPromise;
};

/**
 * 判断当前缓存的 token 是否需要刷新。
 * @returns {boolean} true 表示需要重新获取
 */
const shouldRefreshGuestToken = () => {
	if (!cachedGuestToken) {
		return true;
	}

	if (!cachedGuestTokenExp) {
		return true;
	}

	const nowSeconds = Math.floor(Date.now() / 1000);
	return cachedGuestTokenExp - nowSeconds <= TOKEN_REFRESH_THRESHOLD_SECONDS;
};

/**
 * 将新 token 缓存到内存，便于后续复用。
 * @param {string} token 新获取的 token
 */
const cacheGuestToken = (token) => {
	const exp = getJwtExp(token);
	cachedGuestToken = token;
	cachedGuestTokenExp = exp ?? 0;
	persistGuestToken(cachedGuestToken, cachedGuestTokenExp);
};

/**
 * 请求信令服务生成新 token 并写入缓存。
 * @returns {Promise<string|null>} 成功返回 token
 */
const fetchAndCacheGuestToken = async () => {
	const response = await getGuestToken();
	const token = response?.token ?? null;
	if (token) {
		cacheGuestToken(token);
	}
	return token;
};

/**
 * 初始化缓存，优先复用 sessionStorage 中的 token。
 */
const initializeCachedToken = () => {
	try {
		const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
		const storedExp = sessionStorage.getItem(TOKEN_EXP_STORAGE_KEY);
		cachedGuestToken = storedToken || null;
		cachedGuestTokenExp = storedExp ? Number(storedExp) || 0 : 0;
	} catch {
		cachedGuestToken = null;
		cachedGuestTokenExp = 0;
	}
};

/**
 * 将 token 写入 sessionStorage，确保刷新页面后仍可复用。
 * @param {string|null} token token 字符串
 * @param {number} exp 单位秒的过期时间
 */
const persistGuestToken = (token, exp) => {
	try {
		if (token) {
			sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
			sessionStorage.setItem(TOKEN_EXP_STORAGE_KEY, String(exp ?? 0));
		} else {
			sessionStorage.removeItem(TOKEN_STORAGE_KEY);
			sessionStorage.removeItem(TOKEN_EXP_STORAGE_KEY);
		}
	} catch {
		// sessionStorage 不可用时静默失败
	}
};

initializeCachedToken();
